import {HttpClientModule} from '@angular/common/http';
import {NgModule, Injectable, APP_INITIALIZER} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {BrowserModule, Title} from '@angular/platform-browser';
import {BrowserAnimationsModule} from '@angular/platform-browser/animations';
import {ActivatedRouteSnapshot, Data, Params, RouterStateSnapshot} from '@angular/router';
import {EffectsModule} from '@ngrx/effects';
import {RouterStateSerializer, StoreRouterConnectingModule, DefaultRouterStateSerializer} from '@ngrx/router-store';
import { StoreModule} from '@ngrx/store';
import {StoreDevtoolsModule} from '@ngrx/store-devtools';
import {L10nIntlModule, L10nLoader, L10nTranslationModule} from 'angular-l10n';
import * as _ from 'lodash';
import {BsDropdownModule} from 'ngx-bootstrap/dropdown';
import {ModalModule} from 'ngx-bootstrap/modal';
import {PaginationModule} from 'ngx-bootstrap/pagination';
import {PopoverModule} from 'ngx-bootstrap/popover';
import {AppRoutingModule} from './app-routing.module';
import {AppComponent} from './app.component';
import {AuthModule} from './auth/auth.module';
import {ErrorModule} from './error/error.module';
import {RedirectComponent} from './redirect.component';
import {RouterStateUrl, SharedModule} from './shared';
import * as sharedEffects from './shared/effects';
import {reducers} from './shared/reducers';
import {ToastModule} from 'primeng/toast';
import {MessageService} from 'primeng/api';
import {TypeaheadModule} from 'ngx-bootstrap/typeahead';
import {BsDatepickerModule} from 'ngx-bootstrap/datepicker';
import localeBe from '@angular/common/locales/be';
import { registerLocaleData } from '@angular/common';
import {ExcelService} from './shared/services/excel.service';
import {initL10n, l10nConfig} from './i10n-config';
import {AlertConfig, AlertModule} from 'ngx-bootstrap/alert';
import {INgxSelectOptions, NgxSelectModule} from 'ngx-select-ex';
import {
  BillingProductEffect,
  ClientConfigurationEffect,
  CmsEffectEffect,
  ConceptsEffect,
  InvoicesManagementEffect,
  IsSfInvoicedEffect,
  LicencesEffect
} from './shared/effects';
registerLocaleData(localeBe);

@Injectable()
export class CustomRouterStateSerializer implements RouterStateSerializer<RouterStateUrl> {
  serialize(routerState: RouterStateSnapshot): RouterStateUrl {
    return {
      url: routerState.url,
      params: mergeRouteParams(routerState.root, r => r.params),
      queryParams: mergeRouteParams(routerState.root, r => r.queryParams),
      data: mergeRouteData(routerState.root)
    };
  }
}

function mergeRouteParams(route: ActivatedRouteSnapshot, getter: (r: ActivatedRouteSnapshot) => Params): Params {
  if (!route) {
    return {};
  }
  const currentParams = getter(route);
  const primaryChild = _.find(route.children, c => c.outlet === 'primary') || route.firstChild;
  return Object.assign({}, currentParams, mergeRouteParams(primaryChild, getter));
}

function mergeRouteData(route: ActivatedRouteSnapshot): Data {
  if (!route) {
    return {};
  }
  const currentData = route.data;
  const primaryChild = _.find(route.children, c => c.outlet === 'primary') || route.firstChild;
  return Object.assign({}, currentData, mergeRouteData(primaryChild));
}

// const l10nConfig: L10nConfig = {
//   locale: {languages: [{code: 'en', dir: 'ltr'}], defaultLocale: {languageCode: 'en', countryCode: 'US'}, currency: 'USD'},
//   // translation: { providers: [{ type: ProviderType.Static, prefix: './assets/locales/locale-' }], caching: true, missingValue: 'No key' }
//   // TODO: decomment in prod
//   translation: {providers: [{type: ProviderType.Static, prefix: './assets/locales/locale-'}], caching: false , version: environment.appVersion}
// };


// @ts-ignore
// tslint:disable-next-line:variable-name
const CustomSelectOptions: INgxSelectOptions = { // Check the interface for more options
  optionValueField: 'code',
  optionTextField: 'englishName'
};
@NgModule({
  declarations: [AppComponent, RedirectComponent],
  imports: [
    AppRoutingModule,
    AuthModule,
    BrowserAnimationsModule,
    BrowserModule,
    BsDropdownModule.forRoot(),
    BsDatepickerModule.forRoot(),
    NgxSelectModule.forRoot(CustomSelectOptions),
    EffectsModule.forRoot([
      sharedEffects.TestDataEffect,
      sharedEffects.CountriesEffect,
      sharedEffects.CulturesEffect,
      sharedEffects.CurrentClientEffect,
      sharedEffects.CurrentSiteEffect,
      sharedEffects.CurrentSubscriptionEffect,
      sharedEffects.ExternalLinksEffect,
      sharedEffects.ImpersonableUsersEffect,
      sharedEffects.MyClientsEffect,
      sharedEffects.MyOrdersEffect,
      sharedEffects.MySitesEffect,
      sharedEffects.ProductsEffect,
      sharedEffects.PreferencesEffect,
      sharedEffects.RouterEffects,
      sharedEffects.TimezonesEffect,
      sharedEffects.ToastsEffect,
      sharedEffects.UserContextClientEffect,
      sharedEffects.UserContextSiteEffect,
      sharedEffects.UserContextSubscriptionEffect,
      sharedEffects.UserContextImpersonationEffect,
      sharedEffects.UserContextRolesEffect,
      sharedEffects.UsersEffect,
      sharedEffects.UserInfoEffect,
      sharedEffects.ServerEffect,
      sharedEffects.DeviceAvailableEffect,
      sharedEffects.TransportersEffect,
      sharedEffects.PlayerConfigurationEditEffect,
      sharedEffects.PlayerConfigurationDefinitionEffect,
      sharedEffects.PlayerConfigurationUpdateEffect,
      sharedEffects.ProductsCompleteListEffect,
      sharedEffects.QuickSearchEffect,
      sharedEffects.CompaniesLinkContractEffect,
      sharedEffects.ContractLinkEffect,
      sharedEffects.SubscriptionsLinkContractEffect,
      sharedEffects.RulesUpdateEffect,
      sharedEffects.ContractDuplicationEffect,
      sharedEffects.RulesDuplicateEffect,
      sharedEffects.InvoicesListEffect,
      sharedEffects.SupportUsersEffect,
      sharedEffects.ProductionLineEffect,
      sharedEffects.CloseSubscriptionEffect,
      sharedEffects.OfficesEffect,
      sharedEffects.SubListListEffect,
      sharedEffects.ReturnPlayersEffect,
      sharedEffects.ServerURLEffect,
      sharedEffects.ProjectCreationEffect,
      sharedEffects.IsSfInvoicedEffect,
      sharedEffects.ClientConfigurationEffect,
      sharedEffects.CmsEffectEffect,
      sharedEffects.BillingProductEffect,
      sharedEffects.ServicesEffect,
      sharedEffects.ConceptsEffect,
      sharedEffects.LicencesEffect,
      sharedEffects.InvoicesManagementEffect,
    ]),
    ErrorModule,
    FormsModule,
    HttpClientModule,
    ModalModule.forRoot(),
    PaginationModule.forRoot(),
    PopoverModule.forRoot(),
    SharedModule.forRoot(),
    AlertModule.forRoot(),
    //  SlimLoadingBarModule.forRoot(),
    StoreModule.forRoot(reducers,  {
      runtimeChecks: {
        strictStateImmutability: true,
        strictActionImmutability: true
      }
    }),
    StoreRouterConnectingModule.forRoot({ serializer: DefaultRouterStateSerializer }),
    StoreDevtoolsModule.instrument({maxAge: 50}),
    ToastModule,
    L10nTranslationModule.forRoot(l10nConfig),
    L10nIntlModule,
    TypeaheadModule.forRoot()
  ],
  providers: [Title, {provide: RouterStateSerializer, useClass: CustomRouterStateSerializer}, MessageService , ExcelService,
              {
      provide: APP_INITIALIZER,
      useFactory: initL10n,
      deps: [L10nLoader],
      multi: true
    },        AlertConfig
  ],
  bootstrap: [AppComponent]
})
export class AppModule {
  constructor() {}
}
